import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export interface MonthYearPickerProps {
  year: number;
  month: number; // 1-indexed
  onSelect: (year: number, month: number) => void;
  minYear?: number;
}

export function MonthYearPicker({
  year,
  month,
  onSelect,
  minYear = 2022,
}: MonthYearPickerProps) {
  const [pickerYear, setPickerYear] = useState(year);
  const [open, setOpen] = useState(false);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const years = Array.from({ length: currentYear - minYear + 1 }, (_, i) => minYear + i).reverse();

  const canGoNext = year < currentYear || (year === currentYear && month < currentMonth);
  const canGoPrev = year > minYear || (year === minYear && month > 1);

  function goToPrev() {
    if (!canGoPrev) return;
    if (month === 1) {
      onSelect(year - 1, 12);
    } else {
      onSelect(year, month - 1);
    }
  }

  function goToNext() {
    if (!canGoNext) return;
    if (month === 12) {
      onSelect(year + 1, 1);
    } else {
      onSelect(year, month + 1);
    }
  }

  return (
    <div className="flex items-center gap-0.5">
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        disabled={!canGoPrev}
        onClick={goToPrev}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="text-sm font-medium gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" />
            {MONTH_NAMES[month - 1]} {year}
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="filter-popout-glow w-[300px] p-0" align="end">
          <div className="flex">
            <div className="w-[90px] border-r max-h-[240px] overflow-y-auto py-1">
              <p className="px-3 py-1.5 text-xs font-medium text-muted-foreground">Year</p>
              {years.map((y) => (
                <button
                  key={y}
                  onClick={() => setPickerYear(y)}
                  className={cn(
                    "w-full text-left px-3 py-1.5 text-sm hover:bg-accent transition-colors",
                    pickerYear === y && "bg-primary/10 font-semibold"
                  )}
                >
                  {y}
                  {pickerYear === y && <ChevronRight className="h-3 w-3 inline ml-1 opacity-50" />}
                </button>
              ))}
            </div>
            <div className="flex-1 p-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Month</p>
              <div className="grid grid-cols-3 gap-1.5">
                {MONTH_NAMES.map((m, idx) => {
                  const monthNum = idx + 1;
                  const isSelected = pickerYear === year && monthNum === month;
                  const isFuture = pickerYear === currentYear && monthNum > currentMonth;
                  return (
                    <button
                      key={m}
                      disabled={isFuture}
                      onClick={() => {
                        onSelect(pickerYear, monthNum);
                        setOpen(false);
                      }}
                      className={cn(
                        "px-2 py-1.5 rounded text-sm transition-colors",
                        isSelected
                          ? "bg-primary/15 text-primary font-semibold"
                          : "hover:bg-accent",
                        isFuture && "opacity-40 cursor-not-allowed"
                      )}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        disabled={!canGoNext}
        onClick={goToNext}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
